import { useCallback, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

type ConversionEvent =
  | "page_view"
  | "signup_start"
  | "signup_complete"
  | "onboarding_start"
  | "onboarding_complete"
  | "first_campaign"
  | "first_reply"
  | "first_meeting";

interface TrackOptions {
  tenantId?: string;
  metadata?: Record<string, any>;
}

function getUtmParams(): {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
} {
  if (typeof window === "undefined") return {};
  // Prefer persisted first-touch UTMs (set by useAnalyticsTracking) for consistent attribution
  try {
    const stored = localStorage.getItem("ap_utms");
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed && typeof parsed === "object") {
        return {
          utm_source: parsed.utm_source || undefined,
          utm_medium: parsed.utm_medium || undefined,
          utm_campaign: parsed.utm_campaign || undefined,
          utm_content: parsed.utm_content || undefined,
        };
      }
    }
  } catch {
    /* fall through */
  }
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") || undefined,
    utm_medium: params.get("utm_medium") || undefined,
    utm_campaign: params.get("utm_campaign") || undefined,
    utm_content: params.get("utm_content") || undefined,
  };
}

export function useConversionTracking() {
  const tracked = useRef<Set<string>>(new Set());

  const track = useCallback(async (eventType: ConversionEvent, options?: TrackOptions) => {
    // Deduplicate within session
    const key = `${eventType}_${options?.tenantId || "none"}`;
    if (tracked.current.has(key)) return;
    tracked.current.add(key);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const utms = getUtmParams();

      await supabase.from("conversion_events").insert({
        event_type: eventType,
        user_id: user?.id || null,
        tenant_id: options?.tenantId || null,
        metadata: options?.metadata || {},
        referrer: typeof document !== "undefined" ? document.referrer || null : null,
        ...utms,
      } as any);
    } catch (err) {
      console.warn("Conversion tracking error:", err);
    }
  }, []);

  return { track };
}

// Hook to auto-track page views
export function usePageViewTracking(pageName: string) {
  const { track } = useConversionTracking();
  const trackedRef = useRef(false);

  useEffect(() => {
    if (!trackedRef.current) {
      trackedRef.current = true;
      track("page_view", { metadata: { page: pageName } });
    }
  }, [pageName, track]);
}
